import { MARTECH_ENABLED_ENV, shouldSuppressMartech } from '@repo/martech-guard'

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1', '[::1]', '::1']

/**
 * True when the app is being served from a developer's machine — `yarn dev` or a
 * local `yarn preview`.
 *
 * Deliberately a RUNTIME hostname check rather than `import.meta.env.DEV`. That flag
 * is derived from NODE_ENV at BUILD time, so a pipeline that builds a deployed bundle
 * with NODE_ENV=development bakes DEV=true into it — which silently suppressed Segment
 * and Customer.io on account.services.lykadev.com.au. A hostname cannot be poisoned by
 * a build-time env var.
 *
 * Absent a window there is no browser to have loaded an SDK, so we fail closed.
 */
export const isLocalDevServer = (): boolean =>
  typeof window === 'undefined' || LOCAL_HOSTNAMES.includes(window.location.hostname)

/**
 * Whether the Segment / Customer.io / Mixpanel plugins should skip loading.
 *
 * One source of truth for all three, so the SDKs can never disagree about whether
 * martech is on — a partial answer (Segment up, Mixpanel down) is worse than either
 * consistent one, because `mixpanelMiddleware` decorates Segment events with Mixpanel
 * session data.
 *
 * `VITE_MARTECH_ENABLED=true` overrides local suppression, which is how `test:e2e:ci`
 * loads the SDKs against a local preview so Playwright can stub and assert on them.
 */
export const isMartechSuppressed = (): boolean =>
  shouldSuppressMartech({
    isLocalDevServer: isLocalDevServer(),
    env: { [MARTECH_ENABLED_ENV]: import.meta.env.VITE_MARTECH_ENABLED },
  })
